
import { debug, Formsy, george, observable, observer, pr, React, RouteComponentProps, theme, ui } from "aaa";
import { ContactsStore } from "stores/ContactsStore";
import { pushRoute } from "stores/RouterFunctions";
import { history } from "stores/RouterStore";
import { IPostContactResponse } from "types/contact";
import { isValidPhoneNumber } from "util/formValidations";
import { CancelButton } from "../util/CancelButton";
import { NativeRaisedButton } from "./NativeRaisedButton";
import { NewPhoneSigning } from "./NewPhoneSigning";

const OVERLAY_BACKGROUND = "#777";

@observer
export class NewPhone extends React.Component<RouteComponentProps<{}> & {
    contactsStore: ContactsStore
}, {}> {
    @observable buttonDisabled = false;
    @observable signing = false;
    @observable contactResponse?: IPostContactResponse;
    @observable phoneFormatError: string = "";

    private originalBackgroundColor: string | null = "white";

    private formFields?: any;

    purpose = [{ value: "PRIVATE", label: ui.__("contact.type.private") }, { value: "COMPANY", label: ui.__("contact.type.company") }];

    componentDidMount() {
        window.scrollTo(0, 0);
        this.originalBackgroundColor = document.body.style.backgroundColor;
        document.body.style.backgroundColor = OVERLAY_BACKGROUND;
    }

    componentWillUnmount() {
        document.body.style.backgroundColor = this.originalBackgroundColor ?? "white";
    }

    performRequest = async () => {
        if (!this.formFields) {
            return;
        }

        try {
            this.buttonDisabled = true;
            this.contactResponse = await this.props.contactsStore.addPhone(
                this.formFields.countryCallingCode,
                this.formFields.areaCallingCode,
                this.formFields.phoneNumber,
                this.formFields.weekdays || false,
                this.formFields.saturday || false,
                this.formFields.sundayHolidays || false,
                this.formFields.type,
                true,
                false
            );
            this.buttonDisabled = false;
            this.signing = true;
        } catch (err) {
            this.buttonDisabled = false;
            debug.log("### error adding phone request", err);
            pushRoute("/error");
        }
    }

    onSubmit = async (fields: any, resetForm: any, invalidateForm: any) => {
        const phoneNumberString = (fields.countryCallingCode || "") + (fields.areaCallingCode || "") + (fields.phoneNumber || "");

        if (!isValidPhoneNumber(phoneNumberString)) {
            debug.log("### Invalid phone number format", phoneNumberString);
            this.phoneFormatError = ui.__("phone.invalidPhoneFormat");
            return;
        }

        this.phoneFormatError = "";
        this.formFields = fields;
        await this.performRequest();
    }

    onClose = () => {
        // Should this go wrong in some case then try to store history's back route
        // when component mounts, so we can always safely go back
        history.goBack();
    }

    renderModal() {
        if (this.signing) {
            return;
        }

        return (
            <div className="theme-background-color" style={{ display: "flex", flexDirection: "column" }}>
                <div
                    className="theme-modal-overlay"
                    style={{
                        minHeight: "100vh",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center"
                    }}
                >
                    <div
                        className="theme-paper-color"
                        style={{
                            position: "relative",
                            padding: theme.MARGIN,
                            margin: theme.MARGIN,
                            borderRadius: theme.BORDER_RADIUS,
                            minWidth: 320 - 4 * theme.MARGIN // min width is iPhone SE
                        }}
                    >
                        <pr.A11yRefocus>
                            <CancelButton onClick={this.onClose} showRight color={theme.customColors.georgeLightGrey} />
                        </pr.A11yRefocus>
                        <pr.H1_16_TITLE>{ui.__("phone.addNew.title")}</pr.H1_16_TITLE>
                        <br />
                        <george.SectionSubtitleTop>{ui.__("phone.countryCode.info")}</george.SectionSubtitleTop>
                        <Formsy.Form
                            onValidSubmit={this.onSubmit}
                        >
                            <pr.HFlex style={{ width: "100%" }}>
                                <div style={{ flex: 1 }}>
                                    <george.SectionTitle> {ui.__("phone.countryCode")} </george.SectionTitle >
                                    <george.TextFieldFormsy
                                        name="countryCallingCode"
                                        required
                                        validations={{
                                            isNumeric: true,
                                            minLength: 4,
                                            startsWithZeros: (values: any, value?: string) => value && value.startsWith("00")
                                        }}
                                        validationError={ui.__("value.invalidCountryCode")}
                                        hintText="0043"
                                        inputMode="numeric"
                                    />
                                </div>

                                <div style={{ marginLeft: 10, flex: 1 }}>
                                    <george.SectionTitle> {ui.__("phone.areaCode")} </george.SectionTitle >
                                    <george.TextFieldFormsy
                                        name="areaCallingCode"
                                        required
                                        validations="isNumeric"
                                        validationError={ui.__("value.invalid")}
                                        hintText={ui.__("phone.areaCode.example")}
                                        inputMode="numeric"
                                    />
                                </div>
                            </pr.HFlex>
                            <br />

                            <george.SectionTitle>{ui.__("phone.number")}</george.SectionTitle >
                            <george.TextFieldFormsy
                                name="phoneNumber"
                                required
                                validations="isNumeric"
                                validationError={ui.__("value.invalid")}
                                inputMode="numeric"
                                aria-label={ui.__("phone.number")}
                            />
                            {this.phoneFormatError && (
                                <pr.P_14_CONTENT style={{ color: "#ff3b30", marginTop: theme.MARGIN_SMALL }}>
                                    {this.phoneFormatError}
                                </pr.P_14_CONTENT>
                            )}
                            <br />

                            <george.SectionTitle> {ui.__("contact.type")} </george.SectionTitle >
                            <george.DropDownFormsy name="type" items={this.purpose} value="PRIVATE" />
                            <br />
                            <george.SectionTitle> {ui.__("contact.available")} </george.SectionTitle ><br />
                            <george.CheckboxFormsy
                                name="weekdays"
                                label={ui.__("contact.weekdays")}
                            /><br />
                            <george.CheckboxFormsy
                                name="saturday"
                                label={ui.__("contact.saturday")}
                            /><br />
                            <george.CheckboxFormsy
                                name="sundayHolidays"
                                label={ui.__("contact.sundayHolidays")}
                            /><br />

                            <NativeRaisedButton
                                className="theme-action-button-background-color"
                                disabled={this.buttonDisabled}
                                primary
                                fullWidth
                                type="submit"
                                style={{ height: 60, borderRadius: theme.BORDER_RADIUS }}
                                buttonStyle={{ borderRadius: theme.BORDER_RADIUS }}
                                labelStyle={{ fontWeight: "bold", textTransform: "none" }}
                                label={ui.__("common.save")}
                            />
                        </Formsy.Form>
                    </div >
                </div>
            </div>
        );
    }

    renderSigning() {
        if (!this.signing || !this.contactResponse) {
            return;
        }

        return (
            <div
                style={{
                    position: "fixed",
                    marginLeft: "auto",
                    marginRight: "auto",
                    top: 0,
                    zIndex: 100,
                    maxWidth: theme.MAX_APP_WIDTH,
                    width: "100vw",
                    height: "100vh",
                    background: "white"
                }}
            >
                {<NewPhoneSigning
                    contactsStore={this.props.contactsStore}
                    contactResponse={this.contactResponse}
                    onClose={() => this.signing = false}
                    onRetry={async () => { await this.performRequest(); }}
                    onSuccess={async () => {
                        try {
                            this.signing = false;

                            // Reload contacts
                            await this.props.contactsStore.load();
                            this.onClose();
                        } catch (err) {
                            debug.log("### error loading contacts after new email");
                            pushRoute("/error");
                        }
                    }}
                />}
            </div>
        );
    }

    render() {
        return (
            <div>
                {this.renderModal()}
                {this.renderSigning()}
            </div>
        );
    }

}




// WEBPACK FOOTER //
// ./src/components/common/NewPhone.tsx